import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ExternalLink, Radio as SignalIcon, Wifi, ShieldCheck, Users, MapPin,
  Heart, RadioTower, Tent, Signal, Route,
} from "lucide-react";
import SEO from "@/components/SEO";
import { PlanCard, PlansUnavailable, PlansEmpty, PlansLoading } from "@/components/PlanCard";
import { usePlans } from "@/hooks/usePlans";
import { PLAN_TERMS } from "@/data/plans";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const RV_IMAGE = "https://images.unsplash.com/photo-1703913364147-d1c24d986d6f?q=85";

const NZ_CARRIERS = [
  { name: "Spark NZ", tag: "Nationwide coverage" },
  { name: "One NZ", tag: "Great performance in more places" },
  { name: "2degrees", tag: "Strong in rural & regional NZ" },
];

const AU_CARRIERS = [
  { name: "Telstra", tag: "Widest AU coverage" },
  { name: "Optus", tag: "Strong metro & regional AU" },
  { name: "Vodafone", tag: "Native AU access" },
];

const FREEDOM_FEATURES = [
  { icon: <RadioTower size={20} />, title: "Seamlessly switches", text: "Locks to the strongest available network automatically — no settings, no swapping." },
  { icon: <Signal size={20} />, title: "Better coverage in blackspots", text: "One network might have zero bars where another has full signal. You get whichever works." },
  { icon: <ShieldCheck size={20} />, title: "Redundancy in emergencies", text: "A vital backup connection in disasters, breakdowns or anywhere you need to reach help fast." },
  { icon: <Tent size={20} />, title: "Ideal for freedom camping", text: "Built for the remote spots, holiday parks and back-country sites regular SIMs struggle to reach." },
];

const RV_USE_CASES = [
  { icon: <MapPin size={18} />, label: "Stay connected in remote & rural locations" },
  { icon: <Wifi size={18} />, label: "Boost coverage at campsites & freedom spots" },
  { icon: <ShieldCheck size={18} />, label: "Reliable backup in emergencies or disasters" },
  { icon: <Users size={18} />, label: "Stream, work & stay in touch on the road" },
  { icon: <Route size={18} />, label: "Travel NZ & Australia with ease" },
];

const RVMotorhome = () => {
  const { loading, error, plansByCategory, categoryTerms } = usePlans();
  const plans = plansByCategory["rv-motorhome"] || [];
  const rvTerms = PLAN_TERMS.find((t) => t.id === "rv-motorhome-terms");
  const terms = rvTerms && categoryTerms["rv-motorhome-terms"] ? { ...rvTerms, ...categoryTerms["rv-motorhome-terms"] } : rvTerms;

  return (
    <div data-testid="rv-motorhome-page">
      <SEO
        path="/rv-motorhome"
        title="RV & Motorhome Multi-Network Connectivity Plans"
        description="Stay connected on the road. PTT NZ SIMs/eSIMs connect to Spark NZ, One NZ & 2degrees plus native AU access — automatic switching, better blackspot coverage."
        keywords="RV internet plans New Zealand, motorhome WiFi plans New Zealand, campervan mobile data NZ, freedom camping internet NZ, On The Road plans PTT NZ, RV internet Australia, motorhome connectivity Australia, multi-network SIM RV travel, 12 month data plan NZ, long life data SIM no expiry monthly, security camera SIM data plan NZ, bach holiday home internet SIM NZ"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "RV & motorhome multi-network data plans",
          provider: { "@type": "Organization", name: "PTT NZ Limited", url: "https://pttnz.net" },
          areaServed: ["New Zealand", "Australia"],
          name: "PTT NZ On The Road — RV & Motorhome Multi-Network Plans",
          description: "Prepaid multi-network data plans for RV and motorhome travellers, connecting across New Zealand and Australia on a single SIM.",
          offers: plans.filter((p) => p.price != null).map((p) => ({
            "@type": "Offer",
            name: p.name,
            price: Number(p.price).toFixed(2),
            priceCurrency: p.currency || "NZD",
            availability: "https://schema.org/InStock",
            url: "https://pttnz.net/rv-motorhome",
          })),
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--ptt-ink)] text-white">
        <div className="absolute inset-0">
          <img src={RV_IMAGE} alt="Motorhome on an open New Zealand highway" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ptt-ink)] via-[var(--ptt-ink)]/70 to-[var(--ptt-ink)]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/25 bg-white/10 mb-6" data-testid="rv-hero-badge">
            <Heart size={13} className="text-[var(--ptt-teal)]" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest">Becoming the most popular choice for RV &amp; Motorhome users</span>
          </div>
          <h1 className="font-display text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight max-w-3xl">
            Stay connected. <span className="text-[var(--ptt-teal)]">Wherever the road takes you.</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-xl leading-relaxed">
            The smart connectivity choice for RV &amp; Motorhome travellers in New Zealand and Australia — better coverage, more reliable, with custom plans to suit you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#rv-plans" data-testid="rv-hero-cta-plans" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2">See RV &amp; Motorhome plans <ArrowRight size={14} /></a>
            <Link to="/contact?interest=RV%20%2F%20Motorhome%20Connectivity" data-testid="rv-hero-cta-contact" className="px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 border border-white/30 hover:border-[var(--ptt-teal)] hover:text-[var(--ptt-teal)] transition-colors">Talk to us <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ONE SIM. MULTIPLE NETWORKS. TOTAL FREEDOM. */}
      <section className="bg-[var(--ptt-ink)] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[var(--ptt-teal)]">One SIM</div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">Multiple networks. Total freedom.</h2>
              <p className="mt-4 text-white/70 leading-relaxed">
                Our SIMs and eSIMs give you the freedom to seamlessly connect to the strongest available network — automatically. No settings. No swapping. No stress. Better coverage, more reliability, anywhere you roam.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="rv-freedom-features">
              {FREEDOM_FEATURES.map((f) => (
                <div key={f.title} className="border border-white/15 p-5 flex gap-3.5">
                  <div className="w-10 h-10 shrink-0 grid place-items-center bg-white/10 text-[var(--ptt-teal)]">{f.icon}</div>
                  <div>
                    <div className="font-display text-sm font-bold">{f.title}</div>
                    <div className="text-xs text-white/65 mt-1 leading-relaxed">{f.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NZ / AU COVERAGE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow">Coverage</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">One SIM or eSIM. Six networks. Total freedom.</h2>
        <p className="text-[var(--ptt-muted)] mt-3">No settings to change. No manual swapping. Just seamless connectivity.</p>
        <div className="grid sm:grid-cols-2 gap-5 mt-8" data-testid="rv-coverage-grid">
          <div className="border border-[var(--ptt-line)] bg-white p-6">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] mb-4">New Zealand coverage</div>
            <div className="space-y-3">
              {NZ_CARRIERS.map((c) => (
                <div key={c.name} className="flex items-center justify-between border-b border-[var(--ptt-line)] last:border-b-0 pb-3 last:pb-0">
                  <span className="font-display font-bold text-sm">{c.name}</span>
                  <span className="text-xs text-[var(--ptt-muted)]">{c.tag}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[var(--ptt-line)] bg-white p-6">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] mb-4">Australia coverage</div>
            <div className="space-y-3">
              {AU_CARRIERS.map((c) => (
                <div key={c.name} className="flex items-center justify-between border-b border-[var(--ptt-line)] last:border-b-0 pb-3 last:pb-0">
                  <span className="font-display font-bold text-sm">{c.name}</span>
                  <span className="text-xs text-[var(--ptt-muted)]">{c.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PERFECT FOR RV TRAVELLERS */}
      <section className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="eyebrow">Built for the road less travelled</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Perfect for RV &amp; Motorhome travellers.</h2>
          <div className="flex flex-wrap gap-4 mt-8" data-testid="rv-use-cases">
            {RV_USE_CASES.map((u) => (
              <div key={u.label} className="flex items-center gap-2.5 bg-white border border-[var(--ptt-line)] px-4 py-2.5 text-sm font-medium">
                <span className="text-[var(--ptt-teal-deep)]">{u.icon}</span>{u.label}
              </div>
            ))}
          </div>

          <div className="border-2 border-[var(--ptt-teal-deep)] bg-white p-6 lg:p-7 mt-8 flex flex-col sm:flex-row gap-5 items-start" data-testid="rv-carrier-freedom-callout">
            <div className="w-11 h-11 shrink-0 grid place-items-center bg-[var(--ptt-teal)]/15">
              <SignalIcon size={22} className="text-[var(--ptt-teal-deep)]" />
            </div>
            <div>
              <div className="eyebrow">Freedom camping</div>
              <h3 className="font-display text-xl font-bold mt-1.5 leading-snug">Freedom camping doesn&apos;t mean losing signal.</h3>
              <p className="text-sm text-[var(--ptt-ink-soft)] mt-2 leading-relaxed max-w-2xl">
                Not every carrier reaches every freedom camping spot, holiday park or back-country site — one network might have full bars while another has none. Every On The Road SIM automatically switches to whichever of Spark NZ, One NZ or 2degrees has the strongest signal right where you&apos;ve parked up, so you&apos;re never stuck wondering if tonight&apos;s data will actually work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOM 12-MONTH DATA PLANS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Custom data plans</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">Buy the data once. Use it whenever you need it.</h2>
            <p className="mt-4 text-[var(--ptt-muted)] leading-relaxed">
              Beyond the set plans below, we can build a custom data allocation that lasts up to 12 months — or until the data itself runs out, whichever comes first. Buy 100GB (just an example) and it's yours to use across the year, on your own schedule.
            </p>
            <div className="mt-6">
              <Link to="/contact?interest=RV%20%2F%20Motorhome%20Connectivity" data-testid="rv-custom-12month-cta" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Build a custom data plan <ArrowRight size={14} /></Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="rv-custom-plan-benefits">
            {[
              { icon: <Tent size={20} />, title: "Perfect for weekenders", text: "Not on the road every week? Your data just sits there waiting — no need to buy a fresh plan every month you're not using it." },
              { icon: <ShieldCheck size={20} />, title: "Runs a security system", text: "Leave a security camera or alarm running in the RV, motorhome or bach year-round, on the one allocation, without babysitting monthly renewals." },
              { icon: <Route size={20} />, title: "No monthly repurchase", text: "One purchase, up to 12 months of runway. Use it in one trip or spread it across a dozen — it's still the same data." },
              { icon: <SignalIcon size={20} />, title: "Full multi-network access", text: "Same automatic switching across Spark NZ, One NZ & 2degrees in New Zealand and Telstra, Optus & Vodafone in Australia — included by default." },
            ].map((b) => (
              <div key={b.title} className="border border-[var(--ptt-line)] bg-white p-5">
                <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15 text-[var(--ptt-teal-deep)]">{b.icon}</div>
                <div className="font-display text-sm font-bold mt-4">{b.title}</div>
                <div className="text-xs text-[var(--ptt-muted)] mt-1.5 leading-relaxed">{b.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="rv-plans" className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="eyebrow">Set plans</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">Ready-to-go RV & Motorhome plans.</h2>
            <p className="text-[var(--ptt-muted)] mt-3 text-sm">Prefer something ready-made over a custom 12-month allocation? Pick one of the plans below.</p>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <PlansLoading />
          ) : error ? (
            <PlansUnavailable />
          ) : plans.length === 0 ? (
            <PlansEmpty interestLabel="RV / Motorhome Connectivity" />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="rv-plans-grid">
              {plans.map((p) => <PlanCard key={p.id} plan={p} interestLabel="RV / Motorhome Connectivity" />)}
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/contact?interest=RV%20%2F%20Motorhome%20Connectivity" data-testid="rv-cta-custom-plan" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Ask about a custom RV plan <ArrowRight size={14} /></Link>
        </div>

        {/* AD STICKER STRIP */}
        <div className="mt-10 relative border border-[var(--ptt-line)] overflow-hidden aspect-[16/7] lg:aspect-[16/5]" data-testid="rv-hero-strip">
          <img src={RV_IMAGE} alt="Open New Zealand highway — ideal for RV and motorhome multi-network connectivity" className="w-full h-full object-cover" />
          <img
            src="/assets/rv-motorhome-ad.webp"
            alt="PTT NZ On The Road advert — don't settle for one network, get all three, plans from $47 per month"
            data-testid="rv-ad-sticker"
            className="absolute top-[62%] -translate-y-1/2 left-3 sm:left-8 lg:left-14 w-28 sm:w-40 lg:w-52 rotate-[-6deg] shadow-2xl border-[3px] border-white bg-white"
          />
        </div>

        {/* HARDWARE */}
        <div className="grid sm:grid-cols-2 gap-5 mt-8">
          <div className="border border-[var(--ptt-line)] bg-white p-5" data-testid="rv-hardware-partners">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Recommended hardware</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { label: "Peplink" },
                { label: "Teltonika" },
                { label: "Cowfish Technologies", soon: true },
              ].map((h) => (
                <span key={h.label} className="px-3 py-1.5 border border-[var(--ptt-line)] text-xs font-display font-semibold inline-flex items-center gap-1.5">
                  {h.label}
                  {h.soon && <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[var(--ptt-teal-deep)]">soon</span>}
                </span>
              ))}
            </div>
            <p className="text-xs text-[var(--ptt-muted)] mt-3">We&apos;re also looking to recommend and stock Cowfish Technologies hardware soon.</p>
          </div>

          <div className="border border-[var(--ptt-teal-deep)] bg-[var(--ptt-teal)]/10 p-5" data-testid="rv-support-callout">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)]">Dedicated local support</div>
            <div className="font-display text-lg font-bold mt-1">We&apos;re a New Zealand based team</div>
            <p className="text-sm text-[var(--ptt-ink-soft)] mt-1.5 leading-relaxed">Here to support you before, during and after your journey — on the phone or in the Connectivity Portal.</p>
          </div>
        </div>
      </section>

      {/* TERMS */}
      {terms && (
        <section className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
          <div className="eyebrow">Fine print</div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold mt-2 leading-[1.05]">{terms.title}</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="rv-terms-accordion">
            <AccordionItem value={terms.id}>
              <AccordionTrigger className="font-display text-base font-bold">Read the plan terms</AccordionTrigger>
              <AccordionContent className="text-[var(--ptt-ink-soft)] leading-relaxed">
                {terms.intro && <p className="font-semibold mb-2">{terms.intro}</p>}
                <ol className="space-y-2.5 list-none">
                  {terms.clauses.map((clause, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="font-mono-tech text-[var(--ptt-muted)] shrink-0">({String.fromCharCode(97 + i)})</span>
                      <span>{clause}</span>
                    </li>
                  ))}
                </ol>
                {terms.footnote && <p className="mt-3 text-sm text-[var(--ptt-muted)]">{terms.footnote}</p>}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
        <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow">Better coverage. More freedom.</div>
              <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">All bases covered — talk to us about your next trip.</h3>
              <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Whether it's a weekend away or full-time life on the road, we'll help you pick the right plan and hardware.</p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <Link to="/contact?interest=RV%20%2F%20Motorhome%20Connectivity" data-testid="rv-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to us <ArrowRight size={16} /></Link>
              <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="rv-final-cta-portal" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Connectivity Portal <ExternalLink size={14} /></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RVMotorhome;
